import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Slide-out drawer component for dashboard mobile navigation
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - variant: 'admin' | 'business' | 'venue'
 * - children: React nodes
 */
export default function DashboardMobileDrawer({ isOpen, onClose, variant = 'business', children }) {
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);

  // Escape to close, Tab trapped inside the panel, focus returned on close.
  useEffect(() => {
    if (!isOpen) return undefined;

    restoreFocusRef.current = document.activeElement;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    // Move focus into the panel once the entry animation has started.
    const timer = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const firstItem = panel.querySelector(FOCUSABLE);
      (firstItem || panel).focus();
    }, 50);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
      const restore = restoreFocusRef.current;
      if (restore && typeof restore.focus === 'function') restore.focus();
    };
  }, [isOpen, onClose]);

  // Variant-specific close button colors
  const closeButtonColors = {
    admin: 'hover:bg-red-500/20 hover:text-red-400',
    business: 'hover:bg-purple-500/20 hover:text-purple-400',
    venue: 'hover:bg-teal-500/20 hover:text-teal-400',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            onClick={onClose}
            aria-hidden="true"
          />
          
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 h-full w-[85%] max-w-[320px] bg-[#1e1b4b] z-[101] overflow-y-auto"
            style={{ boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)' }}
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Dashboard menu"
            tabIndex={-1}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              type="button"
              aria-label="Close menu"
              className={`absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-xl text-white/60 transition-all ${closeButtonColors[variant]}`}
            >
              <X size={24} aria-hidden="true" />
            </button>
            
            {/* Content */}
            <div className="p-5 pt-16">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
